import React, { useRef, useEffect, useCallback, useState} from 'react';
import LiveLoopTrackComponent from 'components/liveloop/LiveLoopTrackComponent.js';
import TrackMidiInputDropdown from "components/settings/TrackMidiInputDropdown.js";
import TrackMiniControl from './TrackMiniControl.js';
import TriggerMeter from './TriggerMeter.js';
import {useToggledWaveform} from './toggleWaveform.js';
import {useScroll} from './useScroll.js';
import FreezeHistory from './FreezeHistory.js';
import AudioInputOptions from './AudioInputOptions.js';
import LockButton from 'components/LockButton.js';
import {EFFECTS, PIANO_ROLL} from 'constants/editor-bottom-modes.js';
import ConvertTrackModal from 'components/modals/ConvertTrackModal.js';
import { ContextMenu, MenuItem, ContextMenuTrigger } from "react-contextmenu";
import CirklonEditor from '../cirklon/CirklonEditor.js';
import EffectsView from 'components/effects/EffectsView.js';
import StepsContainer from './StepsContainer.js';
import InstallationVolume from '../track/InstallationVolume.js';
import TrackVolumeContainer from 'components/TrackVolumeContainer.js';
import TrackThumbnail from '../TrackThumbnail.js';
import {MIDI, SAMPLER, AUDIO, MULTI_FREEZE} from 'constants/track-types.js';
import WarpButton from '../WarpButton.js';
import TrackHeader from "./TrackHeader.js";
import Colors from 'Colors.js';
import TrackContextMenu from './TrackContextMenu.js';
import {connect} from 'react-redux';
import {
    makeInstrumentRack,
    copyAllSteps,
    makeEffectRack,
    savePreset,
    deleteLocks,
    deleteAutomations,
    transcribeTrack,
    extractStems,
    freezePatterns
} from './trackRightClickActions.js';
import {
    mouseDown,
    setCurrentSoundInList,
    setShowEditorBottom,
    setStepsSelection,
    setStepNumberDraggingFrom,
    setSelectedPianoRollSteps,
    setSelectedEffect,
    setShowPianoRoll,
    setArrangementBottomMode,
    setTrackToConvert,
    setSampleDragging,
    setSampleTagDragging,
    setPresetDragging
} from 'actions/index.js';

import '../../Track.scss';
import '../../Recording.css';

const colors = new Colors();

const TrackComponent = ({
    track,
    isMini,
    onHide,
    isDraggable=true,
    scrollContainer,
    isSelectedTrack,
    showEffects,
    setShowEditorBottom,
    cirklonParameterSelected,
    mouseDown,
    showFreezeHistory,
    setStepsSelection,
    setStepNumberDraggingFrom,
    setSelectedPianoRollSteps,
    resolution,
    setSelectedEffect,
    setShowPianoRoll,
    setArrangementBottomMode,
    setTrackToConvert,
    isConvertingTrack,
    isTrackFrozen,
    sampleDragging,
    presetDragging,
    setSampleDragging,
    setSampleTagDragging,
    setPresetDragging,
    patternLocked,
    tracksToHide,
    setCurrentSoundInList,
    forceShow
}) => { 
    let [showParams, setShowParams] = useState(false);
    const trackRef = useRef(undefined);
    let [dropdownPos, setDropdownPos] = useState(undefined);
    const {hideInfo} = useScroll({
        track,
        tracksToHide,
        trackRef,
        scrollContainer,
        isSelected: isSelectedTrack && !track.pad
    });
    let {trackNumber, sequencerEngine} = track;
    let trackClassName = "track";
    if (trackNumber % 2 === 1) {
        trackClassName += ' odd';
    }

    let id = track.sample !== undefined && track.sample.getId() !== undefined ?
        track.sample.getId() : track.getName();

    let borderColor = "#353535c7";
    let trackColor = id === undefined ? borderColor : colors.getColorForTrack(track);

    let style = {
    };

    useEffect(() => {
        if (trackRef.current) {
            trackRef.current.addEventListener("mousewheel", onWheel);
        }
        return () => {
            if (trackRef.current) {
                trackRef.current.removeEventListener("mousewheel", onWheel);
            }
        }
    }, []);
    const onWheel = (e) => e.stopPropagation();

    let innerColor = '';
    if (isSelectedTrack) {
        borderColor = colors.getColorForTrack(track);
        trackClassName += ' selected';
        style['borderColor'] = borderColor;
    } 

    const {toggleWaveform} = useToggledWaveform({
        isSelectedTrack,
        track,
        sequencerEngine,
        setArrangementBottomMode,
        setShowPianoRoll
    });

    const onMouseUp = useCallback((e) => {
        if (track.state.spatial.showPadSequencer) {
            // e.stopPropagation();
        }
        mouseDown(false);
        let state = sequencerEngine.store.getState();
        let selection = state.steps.stepsSelection;
        if (selection !== undefined && selection.toStep - selection.fromStep > 0) {
            let pattern = track.getEasyPattern();
            let selectedSteps = pattern.getSelectedSteps(selection, resolution);
            if (selectedSteps.length === 0) {
                setSelectedPianoRollSteps(
                    undefined);
            } else {
                setSelectedPianoRollSteps(
                    selectedSteps);
            }
        }

        setStepsSelection(undefined);
        setStepNumberDraggingFrom(undefined);
    }, [track, resolution]);

    useEffect(() => {
        if (isSelectedTrack) {
            clearSelectedEffect();
        }
    }, [isSelectedTrack]);

    const clearSelectedEffect = useCallback(() => {
        sequencerEngine.selectedEffect = undefined;
        setSelectedEffect(undefined);
    }, []);

    useEffect(() => {
        if (!showEffects) {
            sequencerEngine.selectedEffect = undefined;
            setSelectedEffect(undefined);
        }
    }, [showEffects]);

    const onClickTrack = useCallback((e) => {
        if (!isDraggable) {
            // e.stopPropagation();
        }
        if (!track.isEffectsShowing()) {
            clearSelectedEffect();
        }
        setCurrentSoundInList(undefined);
        if (isSelectedTrack) {
            if (sequencerEngine.state.zobject.selectedZObject !== track) {
                sequencerEngine.selectTrack(track.trackNumber);
            }
            return;
        }
        sequencerEngine.selectTrack(track.trackNumber);
    }, [isSelectedTrack, track]);

    const onDrop = useCallback((e) => {
        if (sampleDragging) {
            e.stopPropagation();
            sequencerEngine.auditionSound(sampleDragging, trackNumber);
            setSampleDragging(undefined);
        } else if (presetDragging) {
            e.stopPropagation();
            sequencerEngine.auditionPreset(presetDragging, trackNumber, true, true);
            setPresetDragging(undefined);
        } else if (sequencerEngine.state.selector.sampleTagDragging) {
            let tags = sequencerEngine.state.selector.sampleTagDragging;
            if (!Array.isArray(tags)) {
                tags = [tags];
            }
            sequencerEngine.searchForSounds(
                tags,
                true,
                track);
            setSampleTagDragging(undefined);
        }
    }, [sampleDragging, presetDragging, trackNumber, track]);

    const toggleEditorBottom = useCallback(() => {
        let show = sequencerEngine.state.spatial.showEditorBottom;
        setShowEditorBottom(show ? undefined : PIANO_ROLL);
    }, []);

    const onMouseOver = useCallback(() => {
        if (sequencerEngine.selectedEffect && !track.isEffectsShowing()) {
            clearSelectedEffect();
        }
    }, [track]);

    const toggleLock = useCallback(() => {
        track.togglePatternLock();
    }, [track]);

    if (track.pattern === undefined || (!forceShow && track.pad)) {
        return (<div></div>);
    }

    let innerClassName = "track-inner";
    if (showParams) {
        innerClassName += " showing-cirklon";
    }

    if (track.getTrackType() === AUDIO) {
        trackClassName += " live-loop";
    }
    let inner = '';
    if (!forceShow && tracksToHide.indexOf(track) > -1 && !track.pad) {
        // we've hidden the track so just show the track header
        inner = (
            <div
              //onClick={(e) => e.stopPropagation()}
              onMouseUp={onMouseUp}
              onMouseDown={onClickTrack}
              className={trackClassName + ' hidden'} style={style}>
              <div className="track-side-container">
                <div style={isSelectedTrack ? {borderBottomColor: borderColor} : {}} className="track-side-trap"></div>
                <div style={isSelectedTrack ? {backgroundColor: borderColor} : {}} className="trap-rest"></div>
              </div>
              <div className="track-container">
                <TrackHeader
                  onHide={onHide}
                  showParams={showParams}
                  setShowParams={setShowParams}
                  isSelected={isSelectedTrack}
                  isHidden={true}
                  borderColor={borderColor}
                  trackColor={trackColor}
                  track={track}
                  sequencerEngine={sequencerEngine}/>
              </div>
            </div>);
    } else {
        inner = (<div
                   onMouseUp={onMouseUp}
                   onMouseDown={onClickTrack}
                   className={trackClassName} style={style}>
                   <div className="track-side-container">
                     <div style={isSelectedTrack ? {borderBottomColor: borderColor} : {}} className="track-side-trap"></div>
                     <div style={isSelectedTrack ? {backgroundColor: borderColor} : {}} className="trap-rest"></div>
                   </div>
                   <div className="track-container">
                     <TrackHeader
                       onHide={onHide}
                       showParams={showParams}
                       setShowParams={setShowParams}
                       isSelected={isSelectedTrack}
                       borderColor={borderColor}
                       trackColor={trackColor}
                       track={track}
                       sequencerEngine={sequencerEngine}/>
                     <div style={isSelectedTrack && showEffects ? {backgroundColor: innerColor} : {}} className={innerClassName}>
                       <div className='unselected youtube-container'>
                         <TrackThumbnail 
                           className="track-thumbnail"
                           track={track}
                           sample={track.sample}
                           onClickThumbnail={toggleEditorBottom}
                         >
                           <div className="volume-and-inputs">
                             {track.getTrackType() === AUDIO ||
                              track.getTrackType() === MIDI ? <AudioInputOptions track={track}/> : ''}
                             <InstallationVolume
                               sequencerEngine={sequencerEngine}
                               track={track}/>
                           </div>
                         </TrackThumbnail>
                       </div>
                       <TrackVolumeContainer
                         sequencerEngine={sequencerEngine}
                         isSelectedTrack={isSelectedTrack}
                         trackNumber={trackNumber}
                         trackData={track}/>
                       {track.getTrackType() === AUDIO ?
                        <LiveLoopTrackComponent
                          track={track}/> :
                        showParams ?
                        <CirklonEditor color={trackColor} track={track}/> : 
                       <StepsContainer
                           key={1}
                          isSelectedTrack={isSelectedTrack}
                          sequencerEngine={sequencerEngine}
                           onClickTrack={onClickTrack}
                          store={sequencerEngine.store}
                          trackNumber={track.trackNumber}
                          id={id}
                          trackColor={trackColor}
                          borderColor={borderColor}
                          sample={track.sample}
                          track={track}
                          trackData={track}
                       />}
                       {/*  <TrackMiniControl key={2} color={trackColor} track={track}/>*/}

                     </div>
                   </div>
                 </div>);
    };
    

    return (
        <div
          onClick={(e) => e.stopPropagation()}
          onMouseOver={onMouseOver}
          ref={trackRef}
          onDragOver={(e) => e.preventDefault()}
          onDrop={onDrop}
        >
          {isConvertingTrack ? 
           <ConvertTrackModal
             show={isConvertingTrack}
             track={track}
             onClickDone={(isRack) => {
                 setTrackToConvert(undefined);
                 if (isRack) {
                     track.convertToInstrumentRack();
                 }  else {
                     track.convertToSampledInstrument();
                 }
             }} 
             onClickCancel={() => setTrackToConvert(undefined)}
           /> : ''}
          {hideInfo ? <div style={{height: hideInfo.trackHeight}} className="ghost-track"/> : inner}
        </div>
    );
}

const mapStateToProps = (state, props) => {
    let track = props.track;
    let _track = track;
    if (track && track.tracks && track.tracks[0]) {
        _track = track.tracks[0].track;
    }
    
    let subSequenceLocks = state.tracks.trackPatternLocks[props.track.subSequence.id] || {};
    let patterns = subSequenceLocks[props.track.trackNumber] || {};
    let locked = patterns[props.track.currentPattern] || false;

    let isSelectedTrack =state.mix.selectedTrack === props.track.trackNumber; 
    return {
        patternLocked: locked,
        tracksToHide: state.tracks.tracksToHide,
        trackId: track.id,
        id: _track.presetName ? _track.presetName :
            _track.sample !== undefined && _track.sample.getId() !== undefined ?
            _track.sample.getId() :
            _track.getName(),
        isTrackFocused: state.sequencer.trackFocused === props.track.trackNumber,
        isSelectedTrack,
        resolution: state.sequencer.displayResolution,
        isConvertingTrack: state.tracks.trackToConvert === props.track,
        sampleDragging: state.selector.sampleDragging,
        presetDragging: state.presets.presetDragging,
        instrument: props.track.instrument,
        tracks: props.track.tracks,
        showEffects: state.tracks.showTrackEffects[props.track.trackNumber],
        showFreezeHistory: state.tracks.showTrackFreezeHistory[props.track.id],
        cirklonParameterSelected: state.cirklon.cirklonParameterSelected[props.track.id],
        pad: props.track.pad
    };
};

const mapDispatchToProps = (dispatch) => {
    return {
        mouseDown: m => dispatch(mouseDown(m)),
        setSelectedSteps: (steps) => dispatch(setSelectedPianoRollSteps(steps)),
        setStepNumberDraggingFrom: (stepNumber) => dispatch(setStepNumberDraggingFrom(stepNumber)),
        setSelectedPianoRollSteps: (steps) => dispatch(setSelectedPianoRollSteps(steps)),
        setSelectedEffect: (id) => dispatch(setSelectedEffect(id)),
        setStepsSelection: (steps) => dispatch(setStepsSelection(steps)),
        setShowPianoRoll: (show) => dispatch(setShowPianoRoll(show)),
        setArrangementBottomMode: mode => dispatch(setArrangementBottomMode(mode)),
        setTrackToConvert: track => dispatch(setTrackToConvert(track)),
        setSampleDragging : (sample) => dispatch(setSampleDragging(sample)),
        setPresetDragging: (sample) => dispatch(setPresetDragging(sample)),
        setSampleTagDragging: x => dispatch(setSampleTagDragging(x)),
        setCurrentSoundInList: x => dispatch(setCurrentSoundInList(x)),
        setShowEditorBottom: x => dispatch(setShowEditorBottom(x))
    };
};

export default connect(mapStateToProps, mapDispatchToProps)(TrackComponent);
